import { BaseAdapter } from "./base-adapter";
import {
  type TEvent,
  EventType,
  type IPageViewEvent,
  type IButtonClickEvent,
  type IFormSubmissionEvent,
  type IProductViewEvent,
  type IAddToCartEvent,
  type IRemoveFromCartEvent,
  type ICheckoutStartedEvent,
  type ICheckoutCompletedEvent,
  type ISearchEvent,
  type IUserSignupEvent,
  type IUserLoginEvent,
  type ICustomEvent,
  type IItemPurchasedEvent,
  type IUpdateCartEvent,
  type ICustomerRegisteredEvent,
  type ICustomerLoggedInEvent,
  type IShopifyCheckoutUpdatedEvent,
  type IShopifyAbandonedCheckoutEvent,
  type IShopifyOrderFulfilledEvent,
  type IShopifyCheckoutStartedEvent,
  type IShopifyAddToCartEvent,
  type IShopifyRemoveFromCartEvent,
  type IShopifyUpdateCartEvent,
  type ISpinformResultEvent,
  type ICouponCopiedEvent,
  type IStartedCheckoutGKEvent,
  type IMobileAddedGKEvent,
  type IAddressSelectedGKEvent,
  type IAddressCompletedGKEvent,
  type IPaymentMethodSelectedGKEvent,
  type IPaymentCompletedGKEvent,
  type IOrderSuccessEvent,
  type IOrderCompletedEvent,
  type IPinCodeAddedGKEvent,
  type IAddressAddedGKEvent,
  type IKPMPPhoneNumberLoggedInEvent,
  type IKPPhoneNumberLoggedInEvent,
  type IKPMPShopifyLoggedInEvent,
  type IKPMPSuccessfullyLoggedOutEvent,
  type IKPWhatsAppLoggedInEvent,
  type IKPMPTruecallerLoggedInEvent,
  type IKPMPWhatsAppLoggedInEvent,
  type IKPTruecallerLoggedInEvent,
  type IKPShopifyLoggedInEvent,
  type IKPSuccessfullyLoggedOutEvent,
} from "../types";
import { EventNames, DEFAULT_CURRENCY } from "../constants";

// Import MoenGage Web SDK
let MoengageSDK: any = null;

// Dynamically import MoenGage SDK only on client side
const loadMoengageSDK = async () => {
  if (typeof window !== "undefined" && !MoengageSDK) {
    try {
      MoengageSDK = await import("@moengage/web-sdk");
      return MoengageSDK;
    } catch (error) {
      console.error("Failed to load MoenGage SDK:", error);
      return null;
    }
  }
  return MoengageSDK;
};

/**
 * Configuration for the MoenGage adapter
 */
export interface MoengageAdapterConfig {
  /**
   * MoenGage App ID (required for SDK initialization)
   */
  appId: string;
  /**
   * Whether to enable user identification
   */
  enableUserIdentification?: boolean;
  /**
   * Debug mode for MoenGage SDK
   */
  debug?: boolean;
  /**
   * Data center region (default: 'dc_01')
   */
  region?: string;
}

/**
 * MoenGage tracking adapter using the official Web SDK
 */
export class MoengageAdapter extends BaseAdapter {
  public readonly name = "moengage";
  private sdk: any = null;

  // /**
  //  * Constructor
  //  * @param config Configuration for the adapter
  //  */
  // constructor(config: MoengageAdapterConfig) {
  //   super(config);
  // }

  /**
   * Initialize the MoenGage adapter using the Web SDK
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      console.log("MoenGage: Already initialized");
      return;
    }

    // Skip initialization if running on server
    if (typeof window === "undefined") {
      console.log("MoenGage: Skipping initialization on server");
      return;
    }

    try {
      // Load the MoenGage SDK
      this.sdk = await loadMoengageSDK();
      if (!this.sdk) {
        console.warn("MoenGage: Failed to load SDK");
        return;
      }

      const appId = this.getConfig<string>("appId");
      if (!appId) {
        console.warn("MoenGage: App ID is required for initialization");
        return;
      }

      // Initialize MoenGage SDK
      const region = this.getConfig<string>("region", "dc_01");
      const config = {
        app_id: appId,
        debug_logs: this.getConfig<boolean>("debug", false) ? 1 : 0,
        cluster: region,
        // Add additional configuration to prevent blocking
        enableSPA: true, // Enable Single Page Application mode
      };

      console.log("MoEngage: Initializing with config:", {
        ...config,
        app_id: "***",
      });
      this.sdk.initialize(config);
      console.log("MoenGage: Successfully initialized with SDK");
      this.initialized = true;
    } catch (error) {
      console.error("MoenGage: Failed to initialize:", error);
    }
  }

  /**
   * Track an event with MoenGage SDK
   * @param event The event to track
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined" || !this.sdk) {
      console.warn(
        "MoenGage: Cannot track event, adapter not initialized or SDK not available",
        event.type,
      );
      return;
    }

    console.log(`MoenGage: Tracking event ${event.type}`);

    switch (event.type) {
      case EventType.PAGE_VIEW:
        this.trackPageView(event as IPageViewEvent);
        break;
      case EventType.BUTTON_CLICK:
        this.trackButtonClick(event as IButtonClickEvent);
        break;
      case EventType.FORM_SUBMISSION:
        this.trackFormSubmission(event as IFormSubmissionEvent);
        break;
      case EventType.PRODUCT_VIEW:
        this.trackProductView(event as IProductViewEvent);
        break;
      case EventType.ADD_TO_CART:
        this.trackAddToCart(event as IAddToCartEvent);
        break;
      case EventType.REMOVE_FROM_CART:
        this.trackRemoveFromCart(event as IRemoveFromCartEvent);
        break;
      case EventType.CHECKOUT_STARTED:
        this.trackCheckoutStarted(event as ICheckoutStartedEvent);
        break;
      case EventType.CHECKOUT_COMPLETED:
        this.trackCheckoutCompleted(event as ICheckoutCompletedEvent);
        break;
      case EventType.SEARCH:
        this.trackSearch(event as ISearchEvent);
        break;
      case EventType.USER_SIGNUP:
        this.trackUserSignup(event as IUserSignupEvent);
        break;
      case EventType.USER_LOGIN:
        this.trackUserLogin(event as IUserLoginEvent);
        break;
      case EventType.CUSTOM:
        this.trackCustomEvent(event as ICustomEvent);
        break;
      // New MoEngage events
      case EventType.ITEM_PURCHASED:
        this.trackItemPurchased(event as IItemPurchasedEvent);
        break;
      case EventType.UPDATE_CART:
        this.trackUpdateCart(event as IUpdateCartEvent);
        break;
      case EventType.CUSTOMER_REGISTERED:
        this.trackCustomerRegistered(event as ICustomerRegisteredEvent);
        break;
      case EventType.CUSTOMER_LOGGED_IN:
        this.trackCustomerLoggedIn(event as ICustomerLoggedInEvent);
        break;
      case EventType.SHOPIFY_CHECKOUT_UPDATED:
        this.trackShopifyCheckoutUpdated(event as IShopifyCheckoutUpdatedEvent);
        break;
      case EventType.SHOPIFY_ABANDONED_CHECKOUT:
        this.trackShopifyAbandonedCheckout(
          event as IShopifyAbandonedCheckoutEvent,
        );
        break;
      case EventType.SHOPIFY_ORDER_FULFILLED:
        this.trackShopifyOrderFulfilled(event as IShopifyOrderFulfilledEvent);
        break;
      case EventType.SHOPIFY_CHECKOUT_STARTED:
        this.trackShopifyCheckoutStarted(event as IShopifyCheckoutStartedEvent);
        break;
      case EventType.SHOPIFY_ADD_TO_CART:
        this.trackShopifyAddToCart(event as IShopifyAddToCartEvent);
        break;
      case EventType.SHOPIFY_REMOVED_FROM_CART:
        this.trackShopifyRemoveFromCart(event as IShopifyRemoveFromCartEvent);
        break;
      case EventType.SHOPIFY_UPDATE_CART:
        this.trackShopifyUpdateCart(event as IShopifyUpdateCartEvent);
        break;
      // SDK events
      case EventType.SPINFORM_RESULT:
        this.trackSpinformResult(event as ISpinformResultEvent);
        break;
      case EventType.COUPON_COPIED:
        this.trackCouponCopied(event as ICouponCopiedEvent);
        break;
      case EventType.STARTED_CHECKOUT_GK:
        this.trackStartedCheckoutGK(event as IStartedCheckoutGKEvent);
        break;
      case EventType.MOBILE_ADDED_GK:
        this.trackMobileAddedGK(event as IMobileAddedGKEvent);
        break;
      case EventType.ADDRESS_SELECTED_GK:
        this.trackAddressSelectedGK(event as IAddressSelectedGKEvent);
        break;
      case EventType.ADDRESS_COMPLETED_GK:
        this.trackAddressCompletedGK(event as IAddressCompletedGKEvent);
        break;
      case EventType.PAYMENT_METHOD_SELECTED_GK:
        this.trackPaymentMethodSelectedGK(
          event as IPaymentMethodSelectedGKEvent,
        );
        break;
      case EventType.PAYMENT_COMPLETED_GK:
        this.trackPaymentCompletedGK(event as IPaymentCompletedGKEvent);
        break;
      case EventType.ORDER_SUCCESS:
        this.trackOrderSuccess(event as IOrderSuccessEvent);
        break;
      case EventType.ORDER_COMPLETED:
        this.trackOrderCompleted(event as IOrderCompletedEvent);
        break;
      case EventType.PIN_CODE_ADDED_GK:
        this.trackPinCodeAddedGK(event as IPinCodeAddedGKEvent);
        break;
      case EventType.ADDRESS_ADDED_GK:
        this.trackAddressAddedGK(event as IAddressAddedGKEvent);
        break;
      case EventType.KP_MP_PHONE_NUMBER_LOGGED_IN:
        this.trackKPMPPhoneNumberLoggedIn(
          event as IKPMPPhoneNumberLoggedInEvent,
        );
        break;
      case EventType.KP_PHONE_NUMBER_LOGGED_IN:
        this.trackKPPhoneNumberLoggedIn(event as IKPPhoneNumberLoggedInEvent);
        break;
      case EventType.KP_MP_SHOPIFY_LOGGED_IN:
        this.trackKPMPShopifyLoggedIn(event as IKPMPShopifyLoggedInEvent);
        break;
      case EventType.KP_MP_SUCCESSFULLY_LOGGED_OUT:
        this.trackKPMPSuccessfullyLoggedOut(
          event as IKPMPSuccessfullyLoggedOutEvent,
        );
        break;
      case EventType.KP_WHATSAPP_LOGGED_IN:
        this.trackKPWhatsAppLoggedIn(event as IKPWhatsAppLoggedInEvent);
        break;
      case EventType.KP_MP_TRUECALLER_LOGGED_IN:
        this.trackKPMPTruecallerLoggedIn(event as IKPMPTruecallerLoggedInEvent);
        break;
      case EventType.KP_MP_WHATSAPP_LOGGED_IN:
        this.trackKPMPWhatsAppLoggedIn(event as IKPMPWhatsAppLoggedInEvent);
        break;
      case EventType.KP_TRUECALLER_LOGGED_IN:
        this.trackKPTruecallerLoggedIn(event as IKPTruecallerLoggedInEvent);
        break;
      case EventType.KP_SHOPIFY_LOGGED_IN:
        this.trackKPShopifyLoggedIn(event as IKPShopifyLoggedInEvent);
        break;
      case EventType.KP_SUCCESSFULLY_LOGGED_OUT:
        this.trackKPSuccessfullyLoggedOut(
          event as IKPSuccessfullyLoggedOutEvent,
        );
        break;
      default:
        // For other events, track as custom events
        this.sdk.track_event(event.type, this.eventToParams(event));
        break;
    }
  }

  /**
   * Track a page view event
   * @param event The page view event
   */
  private trackPageView(event: IPageViewEvent): void {
    if (!this.sdk?.track_event) {
      console.warn("MoenGage track_event not available for PageView event");
      return;
    }

    this.sdk.track_event(EventNames.PAGE_VIEWED, {
      page_path: event.path,
      page_title: event.title,
      page_url: window.location.href,
      referrer: event.referrer || document.referrer,
    });
  }

  /**
   * Track a button click event
   * @param event The button click event
   */
  private trackButtonClick(event: IButtonClickEvent): void {
    this.sdk?.track_event(EventNames.BUTTON_CLICKED, {
      button_id: event.buttonId,
      button_text: event.buttonText,
      location: event.location,
    });
  }

  /**
   * Track a form submission event
   * @param event The form submission event
   */
  private trackFormSubmission(event: IFormSubmissionEvent): void {
    this.sdk?.track_event(EventNames.FORM_SUBMITTED, {
      form_id: event.formId,
      form_name: event.formName,
      success: event.success,
    });
  }

  /**
   * Track a product view event
   * @param event The product view event
   */
  private trackProductView(event: IProductViewEvent): void {
    this.sdk?.track_event(EventNames.PRODUCT_VIEWED, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      category: event.category,
    });
  }

  /**
   * Track an add to cart event
   * @param event The add to cart event
   */
  private trackAddToCart(event: IAddToCartEvent): void {
    const quantity = event.quantity ?? 1;
    this.sdk?.track_event(EventNames.ADDED_TO_CART, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      quantity: quantity,
      variant: event.variant,
      total_value: event.price * quantity,
    });
  }

  /**
   * Track a remove from cart event
   * @param event The remove from cart event
   */
  private trackRemoveFromCart(event: IRemoveFromCartEvent): void {
    this.sdk?.track_event(EventNames.REMOVED_FROM_CART, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      quantity: event.quantity,
      variant: event.variant,
      total_value: event.price * event.quantity,
    });
  }

  /**
   * Track a checkout started event
   * @param event The checkout started event
   */
  private trackCheckoutStarted(event: ICheckoutStartedEvent): void {
    this.sdk?.track_event(EventNames.CHECKOUT_STARTED, {
      cart_value: event.cartValue,
      currency: event.currency || DEFAULT_CURRENCY,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    });
  }

  /**
   * Track a checkout completed event
   * @param event The checkout completed event
   */
  private trackCheckoutCompleted(event: ICheckoutCompletedEvent): void {
    this.sdk?.track_event(EventNames.PURCHASE_COMPLETED, {
      order_id: event.orderId,
      cart_value: event.cartValue,
      currency: event.currency || DEFAULT_CURRENCY,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    });
  }

  /**
   * Track a search event
   * @param event The search event
   */
  private trackSearch(event: ISearchEvent): void {
    this.sdk?.track_event(EventNames.SEARCH, {
      search_term: event.searchTerm,
      results_count: event.resultsCount,
    });
  }

  /**
   * Track a user signup event
   * @param event The user signup event
   */
  private trackUserSignup(event: IUserSignupEvent): void {
    this.sdk?.track_event(EventNames.USER_SIGNED_UP, {
      user_id: event.userId,
      method: event.method,
      success: event.success,
    });

    // Add user attributes if user ID is available and user identification is enabled
    if (
      event.userId &&
      this.getConfig<boolean>("enableUserIdentification", true)
    ) {
      this.sdk?.add_unique_user_id(event.userId);
    }
  }

  /**
   * Track a user login event
   * @param event The user login event
   */
  private trackUserLogin(event: IUserLoginEvent): void {
    this.sdk?.track_event(EventNames.USER_LOGGED_IN, {
      user_id: event.userId,
      method: event.method,
      success: event.success,
    });

    // Add user attributes if user ID is available and user identification is enabled
    if (
      event.userId &&
      this.getConfig<boolean>("enableUserIdentification", true)
    ) {
      this.sdk?.add_unique_user_id(event.userId);
    }
  }

  /**
   * Track a custom event
   * @param event The custom event
   */
  private trackCustomEvent(event: ICustomEvent): void {
    this.sdk?.track_event(event.name, event.properties);
  }

  /**
   * Track an item purchased event
   * @param event The item purchased event
   */
  private trackItemPurchased(event: IItemPurchasedEvent): void {
    this.sdk?.track_event(EventNames.ITEM_PURCHASED, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      quantity: event.quantity,
      variant: event.variant,
      order_id: event.orderId,
    });
  }

  /**
   * Track an update cart event
   * @param event The update cart event
   */
  private trackUpdateCart(event: IUpdateCartEvent): void {
    this.sdk?.track_event(EventNames.UPDATE_CART, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      quantity: event.quantity,
      variant: event.variant,
    });
  }

  /**
   * Track a customer registered event
   * @param event The customer registered event
   */
  private trackCustomerRegistered(event: ICustomerRegisteredEvent): void {
    this.sdk?.track_event(EventNames.CUSTOMER_REGISTERED, {
      user_id: event.userId,
      email: event.email,
      phone: event.phone,
      first_name: event.firstName,
      last_name: event.lastName,
    });

    // Add user attributes if user ID is available and user identification is enabled
    if (
      event.userId &&
      this.getConfig<boolean>("enableUserIdentification", true)
    ) {
      this.sdk?.add_unique_user_id(event.userId);
      if (event.email) this.sdk?.add_email(event.email);
      if (event.firstName) this.sdk?.add_first_name(event.firstName);
      if (event.lastName) this.sdk?.add_last_name(event.lastName);
      if (event.phone) this.sdk?.add_mobile(event.phone);
    }
  }

  /**
   * Track a customer logged in event
   * @param event The customer logged in event
   */
  private trackCustomerLoggedIn(event: ICustomerLoggedInEvent): void {
    this.sdk?.track_event(EventNames.CUSTOMER_LOGGED_IN, {
      user_id: event.userId,
      email: event.email,
      phone: event.phone,
      method: event.method,
    });

    // Add user attributes if user ID is available and user identification is enabled
    if (
      event.userId &&
      this.getConfig<boolean>("enableUserIdentification", true)
    ) {
      this.sdk?.add_unique_user_id(event.userId);
      if (event.email) this.sdk?.add_email(event.email);
      if (event.phone) this.sdk?.add_mobile(event.phone);
    }
  }

  /**
   * Track a Shopify checkout updated event
   * @param event The Shopify checkout updated event
   */
  private trackShopifyCheckoutUpdated(
    event: IShopifyCheckoutUpdatedEvent,
  ): void {
    this.sdk?.track_event(EventNames.SHOPIFY_CHECKOUT_UPDATED, {
      checkout_id: event.checkoutId,
      cart_value: event.cartValue,
      currency: event.currency || DEFAULT_CURRENCY,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    });
  }

  /**
   * Track a Shopify abandoned checkout event
   * @param event The Shopify abandoned checkout event
   */
  private trackShopifyAbandonedCheckout(
    event: IShopifyAbandonedCheckoutEvent,
  ): void {
    this.sdk?.track_event(EventNames.SHOPIFY_ABANDONED_CHECKOUT, {
      checkout_id: event.checkoutId,
      cart_value: event.cartValue,
      currency: event.currency || DEFAULT_CURRENCY,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    });
  }

  /**
   * Track a Shopify order fulfilled event
   * @param event The Shopify order fulfilled event
   */
  private trackShopifyOrderFulfilled(event: IShopifyOrderFulfilledEvent): void {
    this.sdk?.track_event(EventNames.SHOPIFY_ORDER_FULFILLED, {
      order_id: event.orderId,
      cart_value: event.cartValue,
      currency: event.currency || DEFAULT_CURRENCY,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    });
  }

  /**
   * Track a Shopify checkout started event
   * @param event The Shopify checkout started event
   */
  private trackShopifyCheckoutStarted(
    event: IShopifyCheckoutStartedEvent,
  ): void {
    this.sdk?.track_event(EventNames.SHOPIFY_CHECKOUT_STARTED, {
      checkout_id: event.checkoutId,
      cart_value: event.cartValue,
      currency: event.currency || DEFAULT_CURRENCY,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    });
  }

  /**
   * Convert an event to MoenGage parameters
   * @param event The event to convert
   */
  private eventToParams(event: TEvent): Record<string, any> {
    // Remove type and timestamp from the event
    const { type, timestamp, ...params } = event;
    return params;
  }

  /**
   * Set user attributes in MoenGage
   * @param attributes User attributes to set
   */
  public setUserAttributes(attributes: Record<string, any>): void {
    if (!this.initialized || typeof window === "undefined" || !this.sdk) {
      return;
    }

    // Set user attributes
    for (const [key, value] of Object.entries(attributes)) {
      switch (key) {
        case "firstName":
          this.sdk?.add_first_name(value as string);
          break;
        case "lastName":
          this.sdk?.add_last_name(value as string);
          break;
        case "email":
          this.sdk?.add_email(value as string);
          break;
        case "mobile":
          this.sdk?.add_mobile(value as string);
          break;
        case "username":
          this.sdk?.add_user_name(value as string);
          break;
        case "gender":
          this.sdk?.add_gender(value as string);
          break;
        case "birthday":
          this.sdk?.add_birthday(value as Date);
          break;
        case "userId":
          this.sdk?.add_unique_user_id(value as string);
          break;
        default:
          this.sdk?.add_user_attribute(key, value);
          break;
      }
    }
  }

  /**
   * Track a Shopify add to cart event
   * @param event The Shopify add to cart event
   */
  private trackShopifyAddToCart(event: IShopifyAddToCartEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for ShopifyAddToCart event",
        );
        return;
      }

      this.sdk.track_event(EventNames.SHOPIFY_ADD_TO_CART, {
        product_id: event.productId,
        product_name: event.productName,
        price: event.price,
        currency: event.currency || DEFAULT_CURRENCY,
        quantity: event.quantity,
        variant: event.variant,
      });
    } catch (error) {
      console.error("Error tracking ShopifyAddToCart event:", error);
    }
  }

  /**
   * Track a Shopify remove from cart event
   * @param event The Shopify remove from cart event
   */
  private trackShopifyRemoveFromCart(event: IShopifyRemoveFromCartEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for ShopifyRemoveFromCart event",
        );
        return;
      }

      this.sdk.track_event(EventNames.SHOPIFY_REMOVED_FROM_CART, {
        product_id: event.productId,
        product_name: event.productName,
        price: event.price,
        currency: event.currency || DEFAULT_CURRENCY,
        quantity: event.quantity,
        variant: event.variant,
      });
    } catch (error) {
      console.error("Error tracking ShopifyRemoveFromCart event:", error);
    }
  }

  /**
   * Track a Shopify update cart event
   * @param event The Shopify update cart event
   */
  private trackShopifyUpdateCart(event: IShopifyUpdateCartEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for ShopifyUpdateCart event",
        );
        return;
      }

      this.sdk.track_event(EventNames.SHOPIFY_UPDATE_CART, {
        product_id: event.productId,
        product_name: event.productName,
        price: event.price,
        currency: event.currency || DEFAULT_CURRENCY,
        quantity: event.quantity,
        variant: event.variant,
      });
    } catch (error) {
      console.error("Error tracking ShopifyUpdateCart event:", error);
    }
  }

  /**
   * Track a spinform result event
   * @param event The spinform result event
   */
  private trackSpinformResult(event: ISpinformResultEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for SpinformResult event",
        );
        return;
      }

      this.sdk.track_event(EventNames.SPINFORM_RESULT, {
        result: event.result,
      });
    } catch (error) {
      console.error("Error tracking SpinformResult event:", error);
    }
  }

  /**
   * Track a coupon copied event
   * @param event The coupon copied event
   */
  private trackCouponCopied(event: ICouponCopiedEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for CouponCopied event",
        );
        return;
      }

      this.sdk.track_event(EventNames.COUPON_COPIED, {
        coupon_code: event.couponCode,
      });
    } catch (error) {
      console.error("Error tracking CouponCopied event:", error);
    }
  }

  /**
   * Track a started checkout GK event
   * @param event The started checkout GK event
   */
  private trackStartedCheckoutGK(event: IStartedCheckoutGKEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for StartedCheckoutGK event",
        );
        return;
      }

      this.sdk.track_event(EventNames.STARTED_CHECKOUT_GK, {
        cart_value: event.cartValue,
        currency: event.currency || DEFAULT_CURRENCY,
      });
    } catch (error) {
      console.error("Error tracking StartedCheckoutGK event:", error);
    }
  }

  /**
   * Track a mobile added GK event
   * @param event The mobile added GK event
   */
  private trackMobileAddedGK(event: IMobileAddedGKEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for MobileAddedGK event",
        );
        return;
      }

      this.sdk.track_event(EventNames.MOBILE_ADDED_GK, {
        mobile: event.mobile,
      });
    } catch (error) {
      console.error("Error tracking MobileAddedGK event:", error);
    }
  }

  /**
   * Track an address selected GK event
   * @param event The address selected GK event
   */
  private trackAddressSelectedGK(event: IAddressSelectedGKEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for AddressSelectedGK event",
        );
        return;
      }

      this.sdk.track_event(EventNames.ADDRESS_SELECTED_GK, {
        address_id: event.addressId,
      });
    } catch (error) {
      console.error("Error tracking AddressSelectedGK event:", error);
    }
  }

  /**
   * Track an address completed GK event
   * @param event The address completed GK event
   */
  private trackAddressCompletedGK(event: IAddressCompletedGKEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for AddressCompletedGK event",
        );
        return;
      }

      this.sdk.track_event(EventNames.ADDRESS_COMPLETED_GK, {
        address_id: event.addressId,
      });
    } catch (error) {
      console.error("Error tracking AddressCompletedGK event:", error);
    }
  }

  /**
   * Track a payment method selected GK event
   * @param event The payment method selected GK event
   */
  private trackPaymentMethodSelectedGK(
    event: IPaymentMethodSelectedGKEvent,
  ): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for PaymentMethodSelectedGK event",
        );
        return;
      }

      this.sdk.track_event(EventNames.PAYMENT_METHOD_SELECTED_GK, {
        payment_method: event.paymentMethod,
      });
    } catch (error) {
      console.error("Error tracking PaymentMethodSelectedGK event:", error);
    }
  }

  /**
   * Track a payment completed GK event
   * @param event The payment completed GK event
   */
  private trackPaymentCompletedGK(event: IPaymentCompletedGKEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for PaymentCompletedGK event",
        );
        return;
      }

      this.sdk.track_event(EventNames.PAYMENT_COMPLETED_GK, {
        amount: event.amount,
        currency: event.currency || DEFAULT_CURRENCY,
      });
    } catch (error) {
      console.error("Error tracking PaymentCompletedGK event:", error);
    }
  }

  /**
   * Track an order success event
   * @param event The order success event
   */
  private trackOrderSuccess(event: IOrderSuccessEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for OrderSuccess event",
        );
        return;
      }

      this.sdk.track_event(EventNames.ORDER_SUCCESS, {
        order_id: event.orderId,
        amount: event.amount,
        currency: event.currency || DEFAULT_CURRENCY,
      });
    } catch (error) {
      console.error("Error tracking OrderSuccess event:", error);
    }
  }

  /**
   * Track an order completed event
   * @param event The order completed event
   */
  private trackOrderCompleted(event: IOrderCompletedEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for OrderCompleted event",
        );
        return;
      }

      this.sdk.track_event(EventNames.ORDER_COMPLETED, {
        order_id: event.orderId,
        amount: event.amount,
        currency: event.currency || DEFAULT_CURRENCY,
      });
    } catch (error) {
      console.error("Error tracking OrderCompleted event:", error);
    }
  }

  /**
   * Track a PIN code added GK event
   * @param event The PIN code added GK event
   */
  private trackPinCodeAddedGK(event: IPinCodeAddedGKEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for PinCodeAddedGK event",
        );
        return;
      }

      this.sdk.track_event(EventNames.PIN_CODE_ADDED_GK, {
        pin_code: event.pinCode,
      });
    } catch (error) {
      console.error("Error tracking PinCodeAddedGK event:", error);
    }
  }

  /**
   * Track an address added GK event
   * @param event The address added GK event
   */
  private trackAddressAddedGK(event: IAddressAddedGKEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for AddressAddedGK event",
        );
        return;
      }

      this.sdk.track_event(EventNames.ADDRESS_ADDED_GK, {
        address_id: event.addressId,
      });
    } catch (error) {
      console.error("Error tracking AddressAddedGK event:", error);
    }
  }

  /**
   * Track a KP MP phone number logged in event
   * @param event The KP MP phone number logged in event
   */
  private trackKPMPPhoneNumberLoggedIn(
    event: IKPMPPhoneNumberLoggedInEvent,
  ): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for KPMPPhoneNumberLoggedIn event",
        );
        return;
      }

      this.sdk.track_event(EventNames.KP_MP_PHONE_NUMBER_LOGGED_IN, {
        phone: event.phone,
      });
    } catch (error) {
      console.error("Error tracking KPMPPhoneNumberLoggedIn event:", error);
    }
  }

  /**
   * Track a KP phone number logged in event
   * @param event The KP phone number logged in event
   */
  private trackKPPhoneNumberLoggedIn(event: IKPPhoneNumberLoggedInEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for KPPhoneNumberLoggedIn event",
        );
        return;
      }

      this.sdk.track_event(EventNames.KP_PHONE_NUMBER_LOGGED_IN, {
        phone: event.phone,
      });
    } catch (error) {
      console.error("Error tracking KPPhoneNumberLoggedIn event:", error);
    }
  }

  /**
   * Track a KP MP Shopify logged in event
   * @param event The KP MP Shopify logged in event
   */
  private trackKPMPShopifyLoggedIn(event: IKPMPShopifyLoggedInEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for KPMPShopifyLoggedIn event",
        );
        return;
      }

      this.sdk.track_event(EventNames.KP_MP_SHOPIFY_LOGGED_IN, {
        user_id: event.userId,
      });
    } catch (error) {
      console.error("Error tracking KPMPShopifyLoggedIn event:", error);
    }
  }

  /**
   * Track a KP MP successfully logged out event
   * @param event The KP MP successfully logged out event
   */
  private trackKPMPSuccessfullyLoggedOut(
    event: IKPMPSuccessfullyLoggedOutEvent,
  ): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for KPMPSuccessfullyLoggedOut event",
        );
        return;
      }

      this.sdk.track_event(EventNames.KP_MP_SUCCESSFULLY_LOGGED_OUT, {
        user_id: event.userId,
      });
    } catch (error) {
      console.error("Error tracking KPMPSuccessfullyLoggedOut event:", error);
    }
  }

  /**
   * Track a KP WhatsApp logged in event
   * @param event The KP WhatsApp logged in event
   */
  private trackKPWhatsAppLoggedIn(event: IKPWhatsAppLoggedInEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for KPWhatsAppLoggedIn event",
        );
        return;
      }

      this.sdk.track_event(EventNames.KP_WHATSAPP_LOGGED_IN, {
        phone: event.phone,
      });
    } catch (error) {
      console.error("Error tracking KPWhatsAppLoggedIn event:", error);
    }
  }

  /**
   * Track a KP MP Truecaller logged in event
   * @param event The KP MP Truecaller logged in event
   */
  private trackKPMPTruecallerLoggedIn(
    event: IKPMPTruecallerLoggedInEvent,
  ): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for KPMPTruecallerLoggedIn event",
        );
        return;
      }

      this.sdk.track_event(EventNames.KP_MP_TRUECALLER_LOGGED_IN, {
        phone: event.phone,
      });
    } catch (error) {
      console.error("Error tracking KPMPTruecallerLoggedIn event:", error);
    }
  }

  /**
   * Track a KP MP WhatsApp logged in event
   * @param event The KP MP WhatsApp logged in event
   */
  private trackKPMPWhatsAppLoggedIn(event: IKPMPWhatsAppLoggedInEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for KPMPWhatsAppLoggedIn event",
        );
        return;
      }

      this.sdk.track_event(EventNames.KP_MP_WHATSAPP_LOGGED_IN, {
        phone: event.phone,
      });
    } catch (error) {
      console.error("Error tracking KPMPWhatsAppLoggedIn event:", error);
    }
  }

  /**
   * Track a KP Truecaller logged in event
   * @param event The KP Truecaller logged in event
   */
  private trackKPTruecallerLoggedIn(event: IKPTruecallerLoggedInEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for KPTruecallerLoggedIn event",
        );
        return;
      }

      this.sdk.track_event(EventNames.KP_TRUECALLER_LOGGED_IN, {
        phone: event.phone,
      });
    } catch (error) {
      console.error("Error tracking KPTruecallerLoggedIn event:", error);
    }
  }

  /**
   * Track a KP Shopify logged in event
   * @param event The KP Shopify logged in event
   */
  private trackKPShopifyLoggedIn(event: IKPShopifyLoggedInEvent): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for KPShopifyLoggedIn event",
        );
        return;
      }

      this.sdk.track_event(EventNames.KP_SHOPIFY_LOGGED_IN, {
        user_id: event.userId,
      });
    } catch (error) {
      console.error("Error tracking KPShopifyLoggedIn event:", error);
    }
  }

  /**
   * Track a KP successfully logged out event
   * @param event The KP successfully logged out event
   */
  private trackKPSuccessfullyLoggedOut(
    event: IKPSuccessfullyLoggedOutEvent,
  ): void {
    try {
      if (!this.sdk?.track_event) {
        console.warn(
          "MoenGage track_event not available for KPSuccessfullyLoggedOut event",
        );
        return;
      }

      this.sdk.track_event(EventNames.KP_SUCCESSFULLY_LOGGED_OUT, {
        user_id: event.userId,
      });
    } catch (error) {
      console.error("Error tracking KPSuccessfullyLoggedOut event:", error);
    }
  }
}
